import React, { useMemo, useState } from 'react';

import DataTable from '../shared/data_table';
import Pager from '../shared/pager';
import StatusPill from '../shared/status_pill';
import SummaryCards from '../shared/summary_cards';
import { CheckChip, FilterBar, SelectFilter } from '../shared/controls';
import { EM_DASH, fmt_count, fmt_money, fmt_money_signed } from '../format';
import { DEFAULT_SORT, filter_cells, next_sort, sort_cells } from './logic';

const PAGE_SIZE = 50;
const SORTS = [
  { value: 'abs_diff', label: 'Largest difference' },
  { value: 'teacher', label: 'Teacher name' }
];

const signed_class = (n) => (n > 0 ? 'tp-pos' : n < 0 ? 'tp-neg' : undefined);
// Zero is neither higher nor lower: plain, no sign.
const Signed = ({ value }) =>
  value ? (
    <span className={signed_class(value)}>{fmt_money_signed(value)}</span>
  ) : (
    <span className="tp-muted">{fmt_money(0)}</span>
  );

// A warning, not a block: the row's lines do not add up to its Grand Total yet (awaiting a recompute).
const TiesOutBadge = () => (
  <span title="This teacher's lines do not add up to Grand Total yet; check before paying">
    <StatusPill tone="orange">Lines don't add up</StatusPill>
  </span>
);

const teacher_cell = (c) => (
  <div className="tp-c-who">
    <div className="tp-c-name">{c.teacher_name || EM_DASH}</div>
    <div className="tp-c-meta">ID {c.teacher_id}</div>
    {c.ties_out === false ? (
      <div className="tp-c-meta">
        <TiesOutBadge />
      </div>
    ) : null}
  </div>
);

// One list block: hidden when empty unless `always`.
const ListBlock = ({ title, items, empty, render = (x) => x }) =>
  items.length || empty ? (
    <div className="tp-st-list">
      <div className="tp-st-list-hd">
        {title} <span className="tp-tab-ct">{items.length}</span>
      </div>
      {items.length ? (
        <ul>
          {items.map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key -- display-only, never reordered
            <li key={i}>{render(x)}</li>
          ))}
        </ul>
      ) : (
        <p className="tp-muted">{empty}</p>
      )}
    </div>
  ) : null;

const who = (t) => `${t.teacher_name || 'No name'} (ID ${t.teacher_id})`;

const StatementResults = ({ result, file_name, on_download, on_clear }) => {
  const [filters, setFilters] = useState({ category: '', teacher: '', only_differences: true });
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [offset, setOffset] = useState(0);
  const set_filter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setOffset(0);
  };

  const { summary } = result;
  const shown = useMemo(
    () => sort_cells(filter_cells(result.cells, filters), sort),
    [result.cells, filters, sort]
  );
  const page = shown.slice(offset, offset + PAGE_SIZE);
  const categories = useMemo(() => [...new Set(result.cells.map((c) => c.header))], [result.cells]);

  const cards = [
    { label: 'Teachers compared', value: fmt_count(summary.teachers_compared) },
    {
      label: 'Teachers with differences',
      value: fmt_count(summary.teachers_with_differences),
      tone: summary.teachers_with_differences ? 'neg' : 'pos'
    },
    { label: 'Differences', value: fmt_count(summary.differences) },
    { label: 'Net difference (ours − sheet)', value: fmt_money_signed(summary.net) }
  ];

  const category_columns = [
    { key: 'header', role: 'identifier', label: 'Category', render: (t) => t.header },
    {
      key: 'teachers',
      label: 'Teachers differing',
      align: 'right',
      role: 'amount',
      render: (t) => (t.invalid ? `${t.teachers} (${t.invalid} not a number)` : t.teachers)
    },
    {
      key: 'positive',
      label: 'Ours higher',
      align: 'right',
      role: 'amount',
      render: (t) => <Signed value={t.positive} />
    },
    {
      key: 'negative',
      label: 'Ours lower',
      align: 'right',
      role: 'amount',
      render: (t) => <Signed value={t.negative} />
    },
    {
      key: 'net',
      label: 'Net',
      align: 'right',
      role: 'amount',
      render: (t) => <Signed value={t.net} />
    }
  ];

  // Only Difference has the amount role: on a phone card it is the headline, the two sides labelled.
  const columns = [
    {
      key: 'teacher',
      role: 'identifier',
      label: 'Teacher',
      sort_key: 'teacher',
      render: teacher_cell
    },
    {
      key: 'header',
      label: 'Category',
      render: (c) => <span className="tp-st-cat">{c.header}</span>
    },
    {
      key: 'sheet',
      label: 'Your sheet',
      align: 'right',
      render: (c) =>
        c.invalid ? (
          <span className="tp-neg">“{c.sheet_text}” is not a number</span>
        ) : (
          fmt_money(c.sheet)
        )
    },
    {
      key: 'ours',
      label: 'Our calculation',
      align: 'right',
      render: (c) => fmt_money(c.ours)
    },
    {
      key: 'diff',
      label: 'Difference',
      align: 'right',
      role: 'amount',
      sort_key: 'abs_diff',
      render: (c) => (c.invalid ? EM_DASH : <Signed value={c.diff} />)
    }
  ];

  const notes = [];
  if (result.duplicate_ids.length)
    notes.push(
      `Teacher ID ${result.duplicate_ids.join(', ')} appear more than once in your sheet; the first row was compared.`
    );
  if (result.rows_without_id)
    notes.push(
      `${result.rows_without_id} row(s) in your sheet have no teacher_id and were skipped.`
    );

  return (
    <section className="tp-st-results" aria-label="Comparison results">
      <SummaryCards cards={cards} />
      {summary.unreadable ? (
        <p className="tp-st-note tp-st-unreadable">
          <span className="tp-neg">Cells we couldn't read: {fmt_count(summary.unreadable)}</span>{' '}
          (not numbers, such as #REF!; marked in red below and not counted as differences)
        </p>
      ) : null}

      {notes.map((n) => (
        <div key={n} className="tp-banner tp-banner-warn">
          {n}
        </div>
      ))}

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">Differences by category</span>
            <span className="tp-card-hd-sub">Ours − sheet, beyond ₹1</span>
          </span>
        </div>
        <DataTable
          columns={category_columns}
          rows={summary.categories}
          rowKey={(t) => t.header}
          emptyText="No category differs by more than ₹1."
        />
      </div>

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">What was not matched</span>
          </span>
        </div>
        <div className="tp-st-lists">
          <ListBlock
            title="Teachers only in your sheet"
            items={result.only_in_sheet}
            render={who}
            empty="None"
          />
          <ListBlock
            title="Teachers only in ours"
            items={result.only_in_ours}
            render={who}
            empty="None"
          />
          <ListBlock
            title="Lines don't add up to Grand Total yet"
            items={result.not_tying_out}
            render={(t) => (
              <>
                {who(t)} <TiesOutBadge />
              </>
            )}
          />
          <ListBlock
            title="Your columns we did not recognise"
            items={result.unrecognised}
            empty="None"
          />
          <ListBlock title="Our columns missing from your sheet" items={result.missing_in_sheet} />
          <ListBlock
            title="Newer columns not in your sheet (not compared)"
            items={result.newer_not_in_sheet || []}
          />
          <ListBlock
            title="Our columns not compared"
            items={result.not_compared}
            render={(u) => `${u.header}: not compared: ${u.reason || 'not mapped yet'}`}
          />
        </div>
      </div>

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">Review differences</span>
            <span className="tp-card-hd-sub">
              {file_name ? `${file_name} · ` : ''}
              {shown.length} {shown.length === 1 ? 'row' : 'rows'}
            </span>
          </span>
          <span className="tp-card-hd-acts">
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => on_download(shown)}
              aria-disabled={shown.length ? undefined : 'true'}
            >
              ↓ Differences CSV
            </button>
            <button type="button" className="tp-btn-ghost" onClick={on_clear}>
              Clear comparison
            </button>
          </span>
        </div>
        <div className="tp-st-body">
          <FilterBar label="Filter differences">
            <SelectFilter
              id="tp-st-category"
              label="Category"
              value={filters.category}
              options={categories.map((h) => ({ value: h, label: h.replace(/\s+/g, ' ') }))}
              all_label="All"
              onChange={(v) => set_filter({ category: v })}
            />
            <div className="tp-teacher-search">
              <input
                className="tp-input"
                type="search"
                value={filters.teacher}
                onChange={(e) => set_filter({ teacher: e.target.value })}
                placeholder="Teacher name or ID"
                aria-label="Filter by teacher name or ID"
              />
            </div>
            <CheckChip
              checked={filters.only_differences}
              onChange={() => set_filter({ only_differences: !filters.only_differences })}
            >
              Only differences
            </CheckChip>
            <SelectFilter
              id="tp-st-sort"
              label="Sort"
              value={sort.key}
              options={SORTS}
              active={false}
              onChange={(v) => {
                setSort(v === 'teacher' ? { key: 'teacher', dir: 'asc' } : DEFAULT_SORT);
                setOffset(0);
              }}
            />
          </FilterBar>
        </div>
        <DataTable
          columns={columns}
          rows={page}
          rowKey={(c) => `${c.teacher_id}|${c.key}`}
          sort={sort}
          on_sort={(key) => {
            setSort((s) => next_sort(s, key));
            setOffset(0);
          }}
          emptyText={
            filters.only_differences && !filters.category && !filters.teacher
              ? 'Everything matches within ₹1.'
              : 'No rows for these filters.'
          }
          card_below={620}
        />
      </div>
      <Pager
        offset={offset}
        rows={page.length}
        total={shown.length}
        page_size={PAGE_SIZE}
        on_page={setOffset}
      />
    </section>
  );
};

export default StatementResults;
